import { observer } from "mobx-react"
import { Button, Flex, Table, Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"
import jsonToCsvExport from "json-to-csv-export"

import { IdItem } from "@src/app/components/tableRowItems/idItem"
import {
  CardMassPayoutType,
  PayoutsCardCollection,
} from "@src/pages/payouts/classes/PayoutsCardCollection"
import { CardMassPayoutPayoutResponseStatusEnum } from "@src/shared/api/generated"
import { WithCollection } from "@src/shared/classes/Collection/types"
import { TableFilters } from "@src/shared/componemts/TableFilter"
import { dateTimeFormat } from "@src/shared/date"

type Props = WithCollection<PayoutsCardCollection>

export const PayoutsCardTable = observer(({ collection }: Props) => {
  const columns: ColumnsType<CardMassPayoutType> = [
    {
      dataIndex: "id",
      render: (_, { id }) => <IdItem id={id} />,
      title: "ID",
    },
    {
      dataIndex: "created_at",
      render: (_, { created_at }) => dateTimeFormat(created_at!),
      title: "Date/Time (UTC)",
    },
    {
      dataIndex: "status",
      render: (_, { status }) => {
        const cMap = {
          [CardMassPayoutPayoutResponseStatusEnum.Completed]: "success",
          [CardMassPayoutPayoutResponseStatusEnum.Failed]: "error",
          [CardMassPayoutPayoutResponseStatusEnum.New]: "geekblue",
          [CardMassPayoutPayoutResponseStatusEnum.Pending]: "geekblu",
        }

        return <Tag color={cMap[status] ?? "geekblue"}>{status}</Tag>
      },
      title: "Status",
    },
    {
      dataIndex: "card_number",
      render: (_, { data }) => {
        return <span>{data && data.card_number}</span>
      },
      title: "Card",
    },
    {
      align: "center",
      dataIndex: "amount",
      render: (_, { data }) => {
        return (
          <Typography.Text style={{ textAlign: "center" }}>
            {data && data.amount}
          </Typography.Text>
        )
      },
      title: "Amount",
    },
    {
      align: "center",
      dataIndex: "fee_percent",

      render: (_, { data }) => {
        if (!data?.fee) {
          return "-"
        }

        const { percent } = data.fee

        return (
          <Typography.Text style={{ textAlign: "center" }}>
            {percent ? `${percent.value} %` : "-"}
          </Typography.Text>
        )
      },
      title: "Percent fee",
    },
    {
      align: "center",
      dataIndex: "fee_fixed",

      render: (_, { data }) => {
        if (!data?.fee) {
          return "-"
        }

        const { fix } = data.fee

        return <Typography.Text>{fix ? `${fix.value}` : "-"}</Typography.Text>
      },
      title: "Fixed fee",
    },
    {
      align: "center",
      dataIndex: "fee",

      render: (_, { data }) => {
        if (!data?.fee) {
          return "-"
        }

        const { total_fee } = data.fee

        return <Typography.Text>{total_fee ?? "-"}</Typography.Text>
      },
      title: "Total fee",
    },
    {
      dataIndex: "symbol",
      render: (_, { data }) => {
        return <span>{data && data.symbol}</span>
      },
      title: "Currency",
    },
    {
      dataIndex: "symbol",
      render: (_, { data }) => {
        return <span>{data && data.symbol}</span>
      },
      title: "Currency",
    },
    {
      dataIndex: "error",
      render: (_, { error }) => {
        return error?.code ? (
          <span>{`(${error?.code}) ${error?.message}`}</span>
        ) : null
      },
      title: "Errors",
    },
  ]

  return (
    <Flex gap={32} vertical>
      <TableFilters {...collection.filter.props}></TableFilters>
      <Table
        columns={columns}
        dataSource={collection.items}
        loading={collection.isLoading}
        pagination={collection.pagination}
        scroll={{ x: 1500 }}
      />

      {Boolean(collection.failed.length) && (
        <Button
          id="GENERATED_ID_PAYOUTSTABLE_BUTTON_DOWNLOAD_FAILED_PAYOUTS_2"
          onClick={() =>
            jsonToCsvExport({
              data: collection.forDownload(collection.failed),
              filename: "failed-payouts.csv",
            })
          }
          style={{ margin: "0 auto", width: "fit-content" }}
        >
          Download Failed Payouts
        </Button>
      )}
    </Flex>
  )
})
